import { useState, useEffect } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, useParams, Link, useNavigate } from 'react-router-dom';
import { ChevronDown, Play, X, Phone, Mail, MapPin, ArrowLeft, Calendar, User, Tag, Menu } from 'lucide-react';
import { phases } from './data/phases';
import { products } from './data/products';
import { blogPosts } from './data/blog';
import { calculateInstallment, formatPrice } from './utils/calculator';
import { Navbar } from './components/Navbar';
import { BlogListPage, BlogDetailPage } from './components/Blog';
import { BerandaPage } from './pages/Beranda';
import { TentangPage } from './pages/Tentang';
import { KontakPage } from './pages/Kontak';
import { PanduanLengkapPage } from './pages/PanduanLengkap';

function App() {
  const [selectedPhase, setSelectedPhase] = useState('phase-1');
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showPhaseDropdown, setShowPhaseDropdown] = useState(false);
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const [currentFloor, setCurrentFloor] = useState(1);
  const [tenor, setTenor] = useState(5);
  const [calculation, setCalculation] = useState(0);
  const [showVideo, setShowVideo] = useState(false);
  const [zoomFloorPlan, setZoomFloorPlan] = useState(false);

  // Set page title
  useEffect(() => {
    document.title = 'Katalog Produk - 60+ Pilihan Rumah Mulai Rp 299 Juta | Park Serpong';
  }, []);

  // Auto slideshow every 3 seconds - REMOVED (moved to BerandaPage)

  // 1. Set selectedProduct saat first render
useEffect(() => {
  const filtered = products.filter(p => p.phase_id === selectedPhase);
  setFilteredProducts(filtered);
  if (filtered.length > 0) {
    setSelectedProduct(filtered[0]);
  }
}, []);

// Filter products when phase changes
  useEffect(() => {
    const filtered = products.filter(p => p.phase_id === selectedPhase);
    setFilteredProducts(filtered);
    if (filtered.length > 0) {
      setSelectedProduct(filtered[0]);
    }
  }, [selectedPhase]);

// 2. Hitung saat data benar-benar siap
useEffect(() => {
  if (!selectedProduct) return;

  const result = calculateInstallment(selectedProduct.price, tenor);
  setCalculation(result);
}, [selectedProduct, tenor]);


  const handleCalculateFinal = (years) => {
    setTenor(years);
  }

  const handleCalculate = () => {
    if (!selectedProduct) return;
    console.log("awal");
    const result = calculateInstallment(selectedProduct.price, tenor);
    setCalculation(result);
    console.log("akhir");
  };

  const handleWhatsApp = () => {
    const message = `Halo! Saya ingin bertanya mengenai rumah ${selectedProduct?.name} di Park Serpong lebih lanjut. Terima Kasih`;
    window.open(`https://wa.me/628892273880?text=${encodeURIComponent(message)}`, '_blank');
  };

  if (!selectedProduct) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="App overflow-x-hidden w-full max-w-[100vw]">
      {/* Navbar */}
      <Navbar scrolled={true} currentPage="produk" />

      {/* Produk Section */}
      <section id="produk" className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 pt-24 md:pt-28 pb-16 overflow-x-hidden w-full" data-testid="produk-section">
        <div className="container mx-auto px-4 max-w-full">
          {/* Section Title */}
          <div className="text-center mb-12 px-4">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4" data-testid="produk-title">
              Pilih Rumah Impian Anda
            </h2>
            <p className="text-lg md:text-xl text-gray-600" data-testid="produk-subtitle">
              Temukan properti yang sesuai dengan kebutuhan dan budget Anda
            </p>
          </div>

          <div className="max-w-7xl mx-auto px-2 md:px-4">
            {/* Modern Selector Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mb-8">
              {/* Phase Selector Card */}
              <div className="relative group" data-testid="phase-selector">
                <div className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden">
                  <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-3">
                    <p className="text-white text-sm font-semibold">PILIH FASE</p>
                  </div>
                  <button
                    onClick={() => setShowPhaseDropdown(!showPhaseDropdown)}
                    className="w-full px-6 py-4 flex justify-between items-center hover:bg-gray-50 transition-colors"
                    data-testid="phase-dropdown-btn"
                  >
                    <div className="text-left">
                      <p className="font-bold text-xl text-gray-900">
                        {phases.find((p) => p.id === selectedPhase)?.name || 'Phase 1'}
                      </p>
                      <p className="text-sm text-gray-500">
                        {phases.find((p) => p.id === selectedPhase)?.description}
                      </p>
                    </div>
                    <ChevronDown size={24} className={`text-blue-600 transition-transform ${showPhaseDropdown ? 'rotate-180' : ''}`} />
                  </button>
                  {showPhaseDropdown && (
                    <div className="absolute z-20 w-full bg-white border-t shadow-2xl max-h-80 overflow-y-auto" data-testid="phase-dropdown-menu">
                      {phases.map((phase) => (
                        <button
                          key={phase.id}
                          onClick={() => {
                            setSelectedPhase(phase.id);
                            setShowPhaseDropdown(false);
                          }}
                          className="w-full text-left px-6 py-4 hover:bg-blue-50 transition-colors border-b last:border-b-0"
                          data-testid={`phase-option-${phase.id}`}
                        >
                          <p className="font-bold text-gray-900">{phase.name}</p>
                          <p className="text-sm text-gray-600">{phase.description}</p>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Product Selector Card */}
              <div className="relative group" data-testid="product-selector">
                <div className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden">
                  <div className="bg-gradient-to-r from-green-600 to-green-700 px-6 py-3">
                    <p className="text-white text-sm font-semibold">PILIH TIPE</p>
                  </div>
                  <button
                    onClick={() => setShowProductDropdown(!showProductDropdown)}
                    className="w-full px-6 py-4 flex justify-between items-center hover:bg-gray-50 transition-colors"
                    data-testid="product-dropdown-btn"
                  >
                    <div className="text-left">
                      <p className="font-bold text-xl text-gray-900">{selectedProduct.name}</p>
                      <p className="text-sm text-gray-500">Mulai dari Rp {formatPrice(selectedProduct.price)}</p>
                    </div>
                    <ChevronDown size={24} className={`text-green-600 transition-transform ${showProductDropdown ? 'rotate-180' : ''}`} />
                  </button>
                  {showProductDropdown && (
                    <div className="absolute z-20 w-full bg-white border-t shadow-2xl max-h-80 overflow-y-auto" data-testid="product-dropdown-menu">
                      {filteredProducts.map((product) => (
                        <button
                          key={product.id}
                          onClick={() => {
                            setSelectedProduct(product);
                            setShowProductDropdown(false);
                            setCurrentFloor(1);
                          }}
                          className="w-full text-left px-6 py-4 hover:bg-green-50 transition-colors border-b last:border-b-0"
                          data-testid={`product-option-${product.id}`}
                        >
                          <p className="font-bold text-gray-900">{product.name}</p>
                          <p className="text-sm text-gray-600">Rp {formatPrice(product.price)}</p>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Main Product Display - Two Column Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8" data-testid="product-details-card">
              {/* Left Column - Images & Floor Plans */}
              <div className="space-y-6">
                {/* Product Image with Price Badge */}
                <div className="relative rounded-3xl overflow-hidden shadow-2xl group" data-testid="product-image-container">
                  <img
                    src={selectedProduct.image_url}
                    alt={selectedProduct.name}
                    className="w-full h-[500px] object-cover group-hover:scale-105 transition-transform duration-500"
                    data-testid="product-image"
                  />
                  {/* Price Badge */}
                  <div className="absolute top-6 right-6 bg-white rounded-2xl shadow-xl px-6 py-4">
                    <p className="text-sm text-gray-600 font-semibold">Mulai Dari</p>
                    <p className="text-3xl font-bold text-blue-600">Rp {formatPrice(selectedProduct.price)}</p>
                  </div>
                  {/* Video Play Button */}
                  {selectedProduct.video_url && (
                    <button
                      onClick={() => setShowVideo(true)}
                      className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-0 hover:bg-opacity-40 transition-all duration-300"
                      data-testid="video-play-btn"
                    >
                      <div className="bg-white rounded-full p-6 transform group-hover:scale-110 transition-transform duration-300 shadow-2xl">
                        <Play size={40} className="text-blue-600" />
                      </div>
                    </button>
                  )}
                </div>

                {/* Floor Plans - Compact & Modern */}
                <div className="bg-white rounded-3xl shadow-xl p-6" data-testid="floor-plans-section">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-2xl font-bold text-gray-900" data-testid="floor-plans-title">Denah Lantai</h3>
                    <div className="flex gap-2">
                      {selectedProduct.floor_plans?.map((floor) => (
                        <button
                          key={floor.floor_number}
                          onClick={() => setCurrentFloor(floor.floor_number)}
                          className={`px-4 md:px-5 py-2 rounded-xl font-bold transition-all duration-300 ${
                            currentFloor === floor.floor_number
                              ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg scale-105'
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                          data-testid={`floor-btn-${floor.floor_number}`}
                        >
                          Lantai {floor.floor_number}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div 
                    className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-2xl overflow-hidden cursor-pointer hover:shadow-lg transition-shadow" 
                    data-testid="floor-plan-image-container"
                    onClick={() => setZoomFloorPlan(true)}
                  >
                    <img
                      src={
                        selectedProduct.floor_plans?.find(fp => fp.floor_number === currentFloor)?.image_url || 
                        selectedProduct.floor_plans?.[currentFloor - 1]?.image_url ||
                        ''
                      }
                      alt={`Floor ${currentFloor}`}
                      className="w-full h-[350px] object-contain p-4"
                      data-testid="floor-plan-image"
                    />
                  </div>
                  <p className="text-sm text-gray-500 mt-2 text-center">Klik gambar untuk memperbesar</p>
                </div>
              </div>

              {/* Right Column - Product Info & Calculator */}
              <div className="space-y-6">
                {/* Product Title Card */}
                <div className="bg-white rounded-3xl shadow-xl p-8">
                  <div className="flex items-start justify-between mb-6">
                    <div>
                      <p className="text-blue-600 font-semibold text-lg mb-2">{phases.find((p) => p.id === selectedPhase)?.name}</p>
                      <h2 className="text-4xl font-bold text-gray-900 mb-4" data-testid="product-name">
                        {selectedProduct.name}
                      </h2>
                    </div>
                  </div>

                  {/* Handover Info - Compact */}
                  <div className="bg-gradient-to-r from-blue-50 to-green-50 rounded-2xl p-4 flex items-center" data-testid="handover-info">
                    <div className="bg-blue-600 rounded-xl p-3 mr-4">
                      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-gray-900 font-bold text-lg" data-testid="handover-label">
                        Handover: {selectedProduct.handover_date}
                      </p>
                      <p className="text-sm text-gray-600" data-testid="terms-condition">*Terms & Condition Apply</p>
                    </div>
                  </div>
                </div>

                {/* Calculator Card - Modern & Compact */}
                <div className="bg-gradient-to-br from-white to-blue-50 rounded-3xl shadow-xl p-4 md:p-8" data-testid="installment-calculator">
                  <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 md:mb-6" data-testid="calculator-title">
                    Kalkulator Cicilan
                  </h3>

                  {/* Budget Input - Inline
                  <div className="bg-white rounded-2xl p-4 md:p-6 mb-6 shadow-md">
                    <label className="text-sm font-semibold text-gray-600 mb-3 block" data-testid="calculator-subtitle">
                      Cicilan Per Bulan
                    </label>
                    <div className="flex items-center gap-2 md:gap-3 flex-wrap">
                      <span className="text-2xl md:text-3xl font-bold text-gray-900">Rp</span>
                      <input
                        type="text"
                        value={calculation}
                        onChange={(e) => setMonthlyBudget(e.target.value.replace(/[^0-9.]/g, ''))}
                        readOnly
                        className="text-2xl md:text-3xl font-bold text-blue-600 border-b-4 border-blue-600 bg-transparent flex-1 min-w-0 focus:outline-none"
                        style={{ maxWidth: '100%' }}
                        placeholder={selectedProduct.min_installment.toString()}
                        data-testid="monthly-budget-input"
                      />
                      <span className="text-lg md:text-xl text-gray-600 whitespace-nowrap">{monthlyBudget && parseFloat(monthlyBudget) >= 1000 ? 'M' : 'Juta'}</span>
                    </div>
                    <p className="text-xs md:text-sm text-gray-500 mt-3" data-testid="min-installment-note">
                      Minimal: Rp {formatPrice(selectedProduct.min_installment * 100)} /bulan
                    </p>
                  </div> */}

                  {/* Tenor Selector - Modern Pills */}
                  <div className="mb-6" data-testid="tenor-selector">
                    <label className="text-sm font-semibold text-gray-600 mb-3 block" data-testid="tenor-label">
                      Jangka Waktu
                    </label>
                    <div className="grid grid-cols-4 gap-3">
                      {[5, 10, 15, 20].map((years) => (
                        <button
                          key={years}
                          onClick={() => handleCalculateFinal(years)}
                          className={`py-3 rounded-xl font-bold transition-all duration-300 ${
                            tenor === years
                              ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg scale-105'
                              : 'bg-white text-gray-700 hover:bg-gray-50 shadow-md'
                          }`}
                          data-testid={`tenor-btn-${years}`}
                        >
                          {years} Thn
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Calculation Result - Highlighted */}
                  {calculation && (
                    <div className="bg-gradient-to-r from-green-50 to-blue-50 border-2 border-green-200 rounded-2xl p-6 mb-6" data-testid="calculation-result">
                      <div className="space-y-2">
                        <div className="flex justify-between items-center" data-testid="monthly-payment-result">
                          <span className="text-gray-700 font-semibold">Cicilan/Bulan:</span>
                          <span className="text-2xl font-bold text-green-600">Rp {formatPrice(calculation.monthly_payment)}</span>
                        </div>
                        <div className="flex justify-between items-center text-sm" data-testid="down-payment-result">
                          <span className="text-gray-600">DP (2.5%):</span>
                          <span className="font-semibold text-gray-900">Rp {formatPrice(calculation.down_payment)}</span>
                        </div>
                        <div className="flex justify-between items-center text-sm" data-testid="total-payment-result">
                          <span className="text-gray-600">Total:</span>
                          <span className="font-semibold text-gray-900">Rp {formatPrice(calculation.total_payment)}</span>
                        </div>
                      </div>
                    </div>
                  )}

                  <p className="text-xs text-gray-500 mb-6 text-center" data-testid="calculation-note">
                    *Estimasi dengan DP 2.5% | **Syarat & ketentuan berlaku
                  </p>

                  {/* Action Buttons - Stacked */}
                  <div className="space-y-3">
                    {/* <button
                      onClick={handleCalculate}
                      className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white py-4 rounded-xl font-bold hover:shadow-lg hover:scale-105 transition-all duration-300"
                      data-testid="calculate-btn"
                    >
                      Hitung Sekarang
                    </button> */}

                    <button
                      onClick={handleWhatsApp}
                      className="w-full bg-gradient-to-r from-green-600 to-green-700 text-white py-4 rounded-xl font-bold hover:shadow-lg hover:scale-105 transition-all duration-300 flex items-center justify-center gap-2"
                      data-testid="whatsapp-btn"
                    >
                      <Phone size={20} />
                      Hubungi via WhatsApp
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Video Modal */}
      {showVideo && selectedProduct?.video_url && (
        <div className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4" data-testid="video-modal">
          <div className="bg-white rounded-lg overflow-hidden max-w-4xl w-full">
            <div className="flex justify-between items-center p-4 border-b">
              <h3 className="text-xl font-bold" data-testid="video-modal-title">Video Preview</h3>
              <button
                onClick={() => setShowVideo(false)}
                className="text-gray-600 hover:text-gray-800"
                data-testid="video-close-btn"
              >
                <X size={24} />
              </button>
            </div>
            <div className="aspect-video">
              <iframe
                width="100%"
                height="100%"
                src={selectedProduct.video_url.replace('watch?v=', 'embed/')}
                title="Property Video"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                data-testid="video-iframe"
              ></iframe>
            </div>
          </div>
        </div>
      )}

      {/* Floor Plan Zoom Modal */}
      {zoomFloorPlan && selectedProduct && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4 animate-fade-in"
          data-testid="floor-plan-zoom-modal"
          onClick={() => setZoomFloorPlan(false)}
        >
          <div className="relative w-full h-full flex flex-col items-center justify-center max-w-6xl">
            {/* Floor Selector Buttons - Above Image with Safe Area */}
            <div className="flex gap-2 md:gap-3 mb-4 md:mb-6 z-10" data-testid="zoom-floor-selector">
              {selectedProduct.floor_plans?.map((floor) => (
                <button
                  key={floor.floor_number}
                  onClick={(e) => {
                    e.stopPropagation();
                    setCurrentFloor(floor.floor_number);
                  }}
                  className={`px-5 md:px-6 py-2 md:py-3 rounded-xl font-bold transition-all duration-300 ${
                    currentFloor === floor.floor_number
                      ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-2xl scale-105'
                      : 'bg-white text-gray-700 hover:bg-gray-100 shadow-lg'
                  }`}
                  data-testid={`zoom-floor-btn-${floor.floor_number}`}
                >
                  Lantai {floor.floor_number}
                </button>
              ))}
            </div>

            {/* Zoomed Floor Plan Image - Full Size without White Frame */}
            <div 
              className="relative max-w-full overflow-hidden flex items-center justify-center"
              style={{ maxHeight: 'calc(100vh - 240px)' }}
              onClick={(e) => e.stopPropagation()}
              data-testid="zoom-floor-plan-container"
            >
              <img
                src={
                  selectedProduct.floor_plans?.find(fp => fp.floor_number === currentFloor)?.image_url || 
                  selectedProduct.floor_plans?.[currentFloor - 1]?.image_url ||
                  ''
                }
                alt={`Floor ${currentFloor} - Zoomed`}
                className="max-w-full w-auto h-auto object-contain"
                style={{ maxHeight: 'calc(100vh - 240px)' }}
                data-testid="zoom-floor-plan-image"
              />
            </div>

            {/* Hint Text */}
            <p className="text-white text-xs md:text-sm mt-4 md:mt-5 text-center" data-testid="zoom-hint">
              Klik di luar gambar untuk menutup
            </p>
          </div>
        </div>
      )}

      {/* WhatsApp Floating Widget */}
      <a
        href="https://wa.me/628892273880?text=Halo!%20Saya%20ingin%20bertanya%20mengenai%20rumah%20di%20Park%20Serpong%20lebih%20lanjut.%20Terima%20Kasih"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 bg-green-500 hover:bg-green-600 text-white rounded-full p-4 shadow-2xl hover:shadow-3xl transition-all duration-300 hover:scale-110 z-40 group"
        data-testid="whatsapp-floating-widget"
      >
        <svg 
          className="w-8 h-8 md:w-10 md:h-10" 
          fill="currentColor" 
          viewBox="0 0 24 24"
          data-testid="whatsapp-icon"
        >
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
        
        {/* Pulse Animation Ring */}
        <span className="absolute inset-0 rounded-full bg-green-400 opacity-75 animate-ping"></span>
      </a>
    </div>
  );
}

// Wrapper with Routing
function AppWithRouter() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<BerandaPage />} />
        <Route path="/beranda" element={<BerandaPage />} />
        <Route path="/produk" element={<App />} />
        <Route path="/tentang" element={<TentangPage />} />
        <Route path="/kontak" element={<KontakPage />} />
        <Route path="/blog" element={<BlogListPage />} />
        <Route path="/blog/:slug" element={<BlogDetailPage />} />
        <Route path="/park-serpong-tangerang-selatan" element={<PanduanLengkapPage />} />
        <Route path="/panduan-lengkap" element={<PanduanLengkapPage />} />
      </Routes>
    </BrowserRouter>
  );
}

export default AppWithRouter;